import React, { FC, ReactNode, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { T } from 'src/shared/helpers';
import styled, { keyframes } from 'styled-components';
import { Carousel } from '../../pages/aiStudio/components/presets/carousel';
import { DISCOVERY_PRINTING_PAYLOADS } from './discoveryPrintMethods';
import { ZTooltip } from '../ztooltip';
import { Icon } from '../icon';
import SaveIcon from 'src/shared/material_icons/content/save.svg';
import CloseIcon from 'src/shared/material_icons/navigation/close.svg';
import ExpandLessIcon from 'src/shared/material_icons/navigation/expand_less.svg';
import ExpandMoreIcon from 'src/shared/material_icons/navigation/expand_more.svg';

// ─── Data ────────────────────────────────────────────────────────────────────

const PREDEFINED_PRINTING_METHODS: DiscoveryItem[] = [
    {
        id: 'digital-print',
        name: 'Digital Print',
        description: 'Enables full-color digital printing with all design tools.'
    },
    {
        id: 'screenprinting',
        name: 'Screenprinting',
        description: 'Restricts the palette to a fixed set of spot colors. Ideal for bold, vibrant designs with limited color choices.'
    },
    {
        id: 'monochromie',
        name: 'Monochromie',
        description: 'Single-color printing only. Customers design with one ink color, creating a clean and minimal customization experience.'
    },
    /* Hidden from discovery (not shown in Carlo's set) - kept for restore:
    {
        id: 'cmyk-colors',
        name: 'CMYK Colors',
        description: 'Full-color process printing. Customers get the widest color range for photographic and complex multicolor designs.'
    }
    */
    {
        id: 'limited-color-palette',
        name: 'Limited Color Palette',
        description: 'Restricts the design to a predefined color palette.'
    },
    {
        id: 'pantone-colors',
        name: 'Pantone Colors',
        description: 'Restricts color selection to Pantone swatches. Ensures color accuracy and consistency across different print runs.'
    },
    {
        id: 'repeated-patterns',
        name: 'Repeated Patterns',
        description: 'Turns text and images into a repeating pattern, with adjustable spacing and offset.'
    },
    // These two were AI/Tools pills until 2026-07-06. They belong here: both are print-type SETTINGS the
    // backoffice edits on the print-method page, so a discovery print method that carries the setting demos
    // them through the customizer's real code path (see the rationale in discoveryPrintMethods.ts).
    //
    // AI Vectorizer hides itself on accounts with no VECTORIZE credits — the customizer reports it through the
    // same hidden-pills beacon it uses for already-configured features, because without credits the feature
    // cannot light up at all. Using it spends real credits, exactly as it does live.
    {
        id: 'ai-vectorizer',
        name: 'AI Vectorizer',
        description: 'Converts an uploaded image into clean vector artwork that stays sharp at any print size.'
    },
    {
        id: 'ai-background-remover',
        name: 'AI Background Remover',
        description: 'Strips the background from an uploaded image, leaving a clean cutout to design with.'
    },
    /* Hidden from discovery (not shown in Carlo's set) - kept for restore:
    {
        id: 'uv-printing',
        name: 'UV Printing',
        description: 'UV-cured ink printing on rigid surfaces like wood, metal, and plastic. Supports full-color with raised texture effects.'
    }
    */
    /* Hidden from discovery (not shown in Carlo's set) - kept for restore:
    {
        id: 'heat-transfer',
        name: 'Heat Transfer',
        description: 'Heat-pressed vinyl transfers for simple, durable designs. Best for text, numbers, and basic shapes.'
    }
    */
    {
        id: 'steel-engraving',
        name: 'Steel Engraving',
        description: 'Applies an engraving effect that simulates engraving on steel.'
    },
    /* Hidden from discovery - kept for restore (wood-engraving took this slot):
    {
        id: 'wood-printing',
        name: 'Wood Printing',
        description: 'Full-color printing onto wood surfaces. Customers add text and graphics printed directly on the wood.'
    }
    */
    {
        id: 'wood-engraving',
        name: 'Wood Engraving',
        description: 'Applies an engraving effect that simulates engraving on wood.'
    },
    {
        id: 'glass-engraving',
        name: 'Glass Engraving',
        description: 'Applies an engraving effect that simulates engraving on glass.'
    }
    /* Hidden from discovery (not shown in Carlo's set) - kept for restore:
    {
        id: 'pixelation',
        name: 'Pixelation',
        description: 'Applies a pixelated, mosaic-style effect to designs for a bold, retro low-res look.'
    }
    */
];

const PREDEFINED_PRICING_RULES: DiscoveryItem[] = [
    {
        id: 'per-customization',
        name: 'Price by element count',
        description: 'Flat customization fee that scales with how many elements the design has (1–3 elements $5, 4+ $10).'
    },
    {
        id: 'per-print-area',
        name: 'Price per design side',
        description: 'Fee based on how many sides are customized (1 side $5, 2+ sides $10). Front only costs less than front + back.'
    },
    {
        id: 'per-color',
        name: 'Color surcharge',
        description: 'Surcharge by number of ink colors used (first 2 colors free, 3+ adds $5). Ideal for screenprinting and spot-color workflows.'
    },
    {
        id: 'quantity-tiers',
        name: 'Quantity discount',
        description: 'Volume discount: the per-element customization price drops as the order quantity grows (1+ $10, 10+ $8, 50+ $5).'
    },
    {
        id: 'price-per-text-element',
        name: 'Price per text element',
        description: 'Charge a fixed fee for each text element the customer adds ($5 per text).'
    },
    {
        id: 'price-per-image-upload',
        name: 'Price per image upload',
        description: 'Charge a fixed fee for each image element added to the design ($10 per image).'
    },
    {
        id: 'price-by-element-size',
        name: 'Price by element size',
        description: 'Price each element by its printed area (≤ 50 cm² $5, larger $10). Bigger elements cost more.'
    },
    /* Not in Carlo's discovery set — kept for restore:
    {
        id: 'price-by-canvas-size',
        name: 'Price by canvas size',
        description: 'Incremental fee based on the total print-area (canvas) size — $2 per 10 cm² of available canvas.'
    },
    */
    {
        id: 'price-by-design-size',
        name: 'Price by design size',
        description: 'Price by the total customized area across the design (≤ 50 cm² $3, larger $8).'
    },
    {
        id: 'setup-fee',
        name: 'Setup fee',
        description: 'One-time setup fee applied to the order whenever the product is customized ($15 flat).'
    }
];

const PREDEFINED_AI_TOOLS: DiscoveryItem[] = [
    {
        id: 'ai-image-generation',
        name: 'AI Image Generator & Editor',
        description: 'Lets customers generate and edit images with AI directly inside the customizer.'
    },
    {
        id: 'ai-design-generation',
        name: 'AI Design Generator',
        description: 'AI-powered design suggestions that customers can apply to their products in one click.'
    },
    {
        id: 'ai-product-staging',
        name: 'AI Lifestyle Scenes',
        description: 'Automatically generate lifestyle scenes and backgrounds for product previews using AI.'
    },
    {
        id: 'ai-virtual-try-on',
        name: 'AI Try-On',
        description: 'Lets customers see how the customized product looks on them using AI-powered virtual try-on.'
    }
];

const PREDEFINED_CUSTOMIZATION_TOOLS: DiscoveryItem[] = [
    {
        id: 'shapes-and-masks',
        name: 'Shapes and Masks',
        description: 'Lets customers add shapes and clip images into masks.'
    },
    {
        id: 'fill-background',
        name: 'Fill Background',
        description: 'Let customers fill an area background with a solid color or an uploaded image — comes with a sample color set, so no product background rules are required.'
    },
    {
        id: 'collage-layouts',
        name: 'Collage Layouts',
        description: 'Lets customers arrange images in a grid layout.'
    },
    // Runs a live sandbox even on a product with no 3D model: the customizer generates a plain rectangle
    // procedurally and maps the current design onto it (see the payload in discoveryTools.ts). The AR half
    // cannot be demoed — every AR export is keyed on a real scene — so a caption drawn over the preview
    // (discoverySample3DNotice.tsx) says what both halves do and that the shape is a stand-in.
    // Hidden altogether on products that already own a 3D scene, via the DiscoveryModeHiddenTools beacon.
    {
        id: '3d-preview-ar',
        name: '3D Preview & AR',
        description: 'Lets customers turn the product around and see their customization from every angle.'
    },
    // Temporarily `comingSoon`: the pill is disabled and shows the "Coming soon..." tooltip instead of its
    // description, so it cannot be selected and nothing streams. It USED to open an explainer dialog saying it
    // could not be demoed here (a resolved template is locked to its own model and its images belong to the
    // authoring account, so it 404s on an arbitrary sandbox product) — that copy is kept, unreachable, in
    // useDiscoveryExplainerDialogs. Drop `comingSoon` and re-add the id to EXPLAINER_TOOLS to bring it back.
    {
        id: 'pre-designed-templates',
        name: 'Pre-designed Templates',
        description: 'Lets customers start from a ready-made, editable design instead of an empty product.',
        comingSoon: true
    }
];

// Mirrors the backoffice menu's "Commerce rules" group (see rolePermissions.tsx) minus Pricing rules, which
// is its own discovery category. Extra Options, MOQ and Bulk Variation Forms each run a live sandbox on the
// previewed product; Profanity Filter is explainer-only — it cannot be demoed at all, so selecting it opens a
// descriptive dialog instead (see the rationale in discoveryCommerceRules.ts).
const PREDEFINED_COMMERCE_RULES: DiscoveryItem[] = [
    {
        id: 'extra-options',
        name: 'Extra Options',
        description:
            'Adds a custom form on the product panel or when adding the product to the cart, where customers pick add-ons, each with an optional surcharge.'
    },
    {
        id: 'profanity-filter',
        name: 'Profanity Filter & Image Moderator',
        description: 'Moderates the words and images customers use to personalize your products.'
    },
    {
        id: 'minimum-order-quantity',
        name: 'Minimum Order Quantity',
        description:
            'Sets a minimum order quantity and makes customers order in fixed increments or preset pack sizes, blocking add to cart below the minimum.'
    },
    {
        id: 'bulk-variation-forms',
        name: 'Bulk Variation Forms',
        description:
            'Lets customers order one design across several product variants at once, entering a quantity for each variant in a single grid with per unit and total prices.'
    }
];

const PREDEFINED_MODULES: DiscoveryItem[] = [
    {
        id: 'get-a-quote',
        name: 'Get a Quote',
        description: 'Replaces Add to Cart with a quote request form.'
    }
];

type CategoryId = 'printing' | 'pricing' | 'ai' | 'tools' | 'commerce' | 'modules';

interface CategoryDef {
    id: CategoryId;
    label: string;
    items: DiscoveryItem[];
    color: string;
    saveLabel: string;
    buildYourOwnTooltip?: string;
    hasBuildYourOwn: boolean;
    // Temporarily unavailable: the tab can't be opened and shows a "Coming soon..." tooltip.
    comingSoon?: boolean;
}

// ─── Styled ──────────────────────────────────────────────────────────────────

const PanelContainer = styled.div`
    flex-shrink: 0;
    padding: 10px 24px;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const TabsRow = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
`;

// Collapses/expands the whole panel; sits at the far right of the tabs row.
const CollapseButton = styled.button`
    margin-left: auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 2px;
    border: none;
    border-radius: 4px;
    background: transparent;
    color: #94a3b8;
    cursor: pointer;
    transition:
        color 0.15s ease,
        background 0.15s ease;

    &:hover {
        color: #475569;
        background: #f1f5f9;
    }
`;

// Slim bar shown when the panel is collapsed. Mirrors the tabs row layout so the collapse/expand chevron stays
// in the exact same spot (far right) whether the panel is open or closed.
const CollapsedBar = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    margin-left: -13px;
`;

const CollapsedLabel = styled.button`
    padding: 3px 12px;
    border: none;
    background: transparent;
    color: #64748b;
    font-size: 11px;
    font-weight: 600;
    cursor: pointer;
    white-space: nowrap;
    transition: color 0.15s ease;

    &:hover {
        color: #475569;
    }
`;

const CollapsedCount = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 16px;
    height: 16px;
    padding: 0 5px;
    border-radius: 8px;
    background: #e2e8f0;
    color: #475569;
    font-size: 10px;
    font-weight: 700;
`;

const CategoryTab = styled.button<{ $active: boolean; $color: string; $disabled?: boolean }>`
    padding: 5px 14px;
    border: 1.5px solid ${(props) => (props.$active ? props.$color : 'transparent')};
    border-radius: 6px;
    background: ${(props) => (props.$active ? props.$color + '12' : 'transparent')};
    color: ${(props) => (props.$active ? props.$color : '#64748b')};
    font-size: 12px;
    font-weight: 600;
    cursor: ${(props) => (props.$disabled ? 'not-allowed' : 'pointer')};
    opacity: ${(props) => (props.$disabled ? 0.5 : 1)};
    transition: all 0.15s ease;
    white-space: nowrap;

    &:hover {
        color: ${(props) => (props.$disabled ? '#64748b' : props.$color)};
        background: ${(props) => (props.$disabled ? 'transparent' : props.$color + '08')};
    }
`;

const ContentRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const CarouselWrapper = styled.div`
    flex: 1;
    min-width: 0;
`;

// Uses a transient $disabled prop rather than the native `disabled` attribute so wrapping tooltips still
// receive hover events (browsers suppress mouse events on natively-disabled buttons).
const PillButton = styled.button<{ $selected: boolean; $color: string; $disabled?: boolean }>`
    flex-shrink: 0;
    display: flex;
    align-items: center;
    padding: 6px 16px;
    border: 1.5px solid ${(props) => (props.$selected ? props.$color : '#d1d5db')};
    border-radius: 20px;
    background: ${(props) => (props.$selected ? props.$color + '14' : 'white')};
    color: ${(props) => (props.$selected ? props.$color : '#374151')};
    font-size: 13px;
    font-weight: 500;
    cursor: ${(props) => (props.$disabled ? 'not-allowed' : 'pointer')};
    opacity: ${(props) => (props.$disabled ? 0.45 : 1)};
    transition: all 0.2s ease;
    white-space: nowrap;

    &:hover {
        border-color: ${(props) => (props.$disabled ? '#d1d5db' : props.$color)};
        background: ${(props) => (props.$disabled ? 'white' : props.$color + '0a')};
    }
`;

const EmptyNote = styled.span`
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    padding: 6px 4px;
    color: #94a3b8;
    font-size: 13px;
    font-style: italic;
    white-space: nowrap;
`;

const StickyEnd = styled.div`
    position: sticky;
    right: 0;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    padding-left: 16px;
    background: linear-gradient(to right, transparent 0%, white 12px);
    z-index: 2;
`;

const BuildYourOwnItem = styled.button`
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 6px 16px;
    border: 1.5px dashed #9ca3af;
    border-radius: 20px;
    background: white;
    color: #6b7280;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;

    &:hover {
        border-color: #6b7280;
        color: #374151;
    }
`;

// "Active" reads as a label sitting on the section divider, so it costs no horizontal room in the chip row.
const ActiveDividerLabel = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 2px 0;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #94a3b8;
    white-space: nowrap;

    &::after {
        content: '';
        flex: 1;
        height: 1px;
        background: #e2e8f0;
    }
`;

// Chips share the full width so up to 6 active selections (one per category) fit on a single row.
const ActiveRow = styled.div`
    display: flex;
    align-items: stretch;
    gap: 8px;
`;

// Every chip is the same width, sized so a full set of 6 (one per category) fits the row; with fewer active
// selections the chips keep that width instead of stretching to fill.
const ActivePill = styled.div<{ $color: string }>`
    flex: 0 0 auto;
    width: calc((100% - 5 * 8px) / 6);
    min-width: 0;
    display: inline-flex;
    align-items: stretch;
    border: 1px solid ${(props) => props.$color};
    border-radius: 13px;
    background: ${(props) => props.$color + '14'};
    color: ${(props) => props.$color};
    font-size: 12px;
    font-weight: 500;
    overflow: hidden;
`;

const ActivePillBody = styled.span`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 1px;
    padding: 3px 9px;
`;

const ActivePillName = styled.span`
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

// Always reserves exactly two lines (even when empty or with a shorter/absent description) so the Active row
// keeps a constant height — adding/removing a chip never makes it jump. Longer text is clamped with ellipsis.
const ActivePillDesc = styled.span`
    font-size: 11px;
    font-weight: 400;
    line-height: 1.3;
    color: #64748b;
    height: 29px;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
`;

// Right-hand action rail: save on top, remove (x) below — stacked so it stays narrow across 6 chips.
const PillActions = styled.div<{ $color: string }>`
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    border-left: 1px solid ${(props) => props.$color + '80'};
`;

const PillActionButton = styled.button<{ $color: string }>`
    flex: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0 7px;
    border: none;
    background: transparent;
    color: ${(props) => props.$color};
    cursor: pointer;
    transition: background 0.2s ease;

    &:hover {
        background: ${(props) => props.$color + '22'};
    }

    & + & {
        border-top: 1px solid ${(props) => props.$color + '80'};
    }
`;

// The remove (x) icon is a touch smaller than the save icon so the stacked rail stays compact.
const PillActionIcon = styled(Icon)`
    width: 13px;
    height: 13px;
    min-width: 13px;

    svg {
        width: 13px;
        height: 13px;
    }
`;

const QuantityRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    font-weight: 500;
    color: #64748b;
`;

/**
 * Announces the simulated-quantity presets when they appear.
 *
 * The customizer marks the access point of whatever the merchant selects (see the discovery beacon in
 * zakeke-customizer-ui, components/widgets/beacon). Quantity discount is the one rule whose demo needs a
 * control on THIS side too — the price only moves when the simulated quantity does — and a control that
 * quietly materialises mid-panel is easy to miss, so it announces itself in the same visual language.
 *
 * Wraps the pills rather than living on QuantityRow: the row is a flex child of a column, so it stretches the
 * full panel width and a mark on it would too. This box is a flex ITEM, so it shrinks to its content and the
 * ring lands on the buttons themselves — which is the thing to look at anyway, the label beside it is just
 * naming them.
 *
 * No state to drive it: picking the rule is what mounts this, so the animation plays on mount and replays on
 * every remount (switching rules away and back, or re-picking the pill). Clicking a preset only re-renders,
 * which does not restart a CSS animation — so it flashes when the presets arrive, not on every click.
 */
const QUANTITY_PILLS_FLASH_MS = 2000;

// Scaled down from the customizer's ring (11px at peak): these pills are half the height of a tool button, so
// the same spread would swamp them. Ends transparent, which is also the resting state — no fill-mode needed.
const ringRest = (color: string) => `0 0 0 2px ${color}, 0 0 0 5px ${color}33`;
const ringPeak = (color: string) => `0 0 0 2px ${color}, 0 0 0 8px ${color}55`;

const quantityPillsFlash = (color: string) => keyframes`
    0%, 40%, 80% {
        box-shadow: ${ringRest(color)};
    }
    20%, 60% {
        box-shadow: ${ringPeak(color)};
    }
    100% {
        box-shadow: 0 0 0 2px transparent, 0 0 0 5px transparent;
    }
`;

const QuantityPills = styled.div<{ $color: string }>`
    display: flex;
    align-items: center;
    gap: 8px;
    /* Padding keeps the ring off the pills. Without it the ring's inner edge sits directly against the first
       and last pill's own 1.5px border, and the two lines read as one doubled edge at the ends of the group.
       Square corners and this same padding are what the customizer's price mark uses — the two rings are the
       same signal on two surfaces, so they should not be recognisably different shapes. */
    padding: 4px 8px;
    animation: ${(props) => quantityPillsFlash(props.$color)} ${QUANTITY_PILLS_FLASH_MS}ms ease-in-out;

    /* Drop the movement but keep the mark: here the animation IS the whole signal, so removing it outright
       would leave these users with nothing pointing at the presets. A standing ring says the same thing. */
    @media (prefers-reduced-motion: reduce) {
        animation: none;
        box-shadow: ${(props) => ringRest(props.$color)};
    }
`;

const QuantityPill = styled.button<{ $selected: boolean; $color: string }>`
    flex-shrink: 0;
    padding: 4px 14px;
    border: 1.5px solid ${(props) => (props.$selected ? props.$color : '#d1d5db')};
    border-radius: 16px;
    background: ${(props) => (props.$selected ? props.$color + '14' : 'white')};
    color: ${(props) => (props.$selected ? props.$color : '#374151')};
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        border-color: ${(props) => props.$color};
        background: ${(props) => props.$color + '0a'};
    }
`;

// ─── Types & Props ───────────────────────────────────────────────────────────

export interface DiscoveryItem {
    id: string;
    name: string;
    description?: string;
    // Force-disable the pill regardless of category wiring (e.g. tools whose function isn't hooked up yet).
    disabled?: boolean;
    // Temporarily unavailable: disables the pill and shows a "Coming soon..." tooltip instead of its description.
    comingSoon?: boolean;
}

// Keep backward compat alias
export type PrintTypeItem = DiscoveryItem;

interface DiscoveryModePanelProps {
    // The customizer is fully loaded and ready to receive a selection (booted, and any variant-selection
    // dialog dismissed). While false, every tool pill is disabled — selecting one before the customizer is
    // ready would stream an update the iframe can't apply yet. Defaults to enabled if the prop is omitted.
    customizerReady?: boolean;
    // Ids of AI/Tools pills the customizer reports as already active on the opened product; they are hidden
    // from the carousel (nothing to "discover" about a feature the merchant already has). Defaults to none.
    hiddenTools?: string[];
    selections: Record<string, string | null>;
    onSelect: (category: string, id: string | null) => void;
    onSave: (category: string, item: DiscoveryItem) => void;
    onBuildYourOwn: (category: string) => void;
    // Simulated order quantity, shown only for the quantity-tiers pricing rule (which prices by order
    // quantity). The sandbox has no footer quantity selector, so this drives it.
    quantity?: number;
    quantityPresets?: number[];
    onQuantityChange?: (qty: number) => void;
    // Rendered at the top of the AI Creative Tools tab (CASE 1 "billed via credits" activation banner). The
    // host owns the credit resolution/grant, so it passes the ready-to-render banner (or null) in.
    aiBanner?: ReactNode;
    // Rendered at the top of the Tools tab (Fill Background "import a background gallery" banner for legacy
    // accounts). The host owns the GraphQL detection/import, so it passes the ready-to-render banner (or null) in.
    toolsBanner?: ReactNode;
    // Rendered at the top of the Printing Methods tab (AI Vectorizer credit banner: activate the free trial,
    // remaining balance, or buy more). Same division of labour as the two above — the host resolves credits.
    printingBanner?: ReactNode;
}

// ─── Component ───────────────────────────────────────────────────────────────

// Active-pill description: the item description clamped to two lines. When it overflows (ellipsis), a tooltip on
// hover reveals the full text. Truncation is measured vertically (scrollHeight vs clientHeight) because the clamp
// is multi-line — ZTooltip's built-in isOverflowEnabled only detects single-line (width) overflow. `addContainer`
// keeps ZTooltip from cloning its own ref onto the span, so our measurement ref stays intact.
const ActivePillDescription: FC<{ text: string }> = ({ text }) => {
    const ref = useRef<HTMLSpanElement>(null);
    const [truncated, setTruncated] = useState(false);
    useLayoutEffect(() => {
        const check = () => {
            const el = ref.current;
            setTruncated(el ? el.scrollHeight > el.clientHeight + 1 : false);
        };
        check();
        window.addEventListener('resize', check);
        return () => window.removeEventListener('resize', check);
    }, [text]);
    return (
        <ZTooltip position='bottom' title={truncated ? text : ''} enterDelay={300} exitDelay={0} interactive={false} addContainer>
            <ActivePillDesc ref={ref}>{text}</ActivePillDesc>
        </ZTooltip>
    );
};

export const DiscoveryModePanel: FC<DiscoveryModePanelProps> = ({
    customizerReady = true,
    hiddenTools,
    selections,
    onSelect,
    onSave,
    onBuildYourOwn,
    quantity,
    quantityPresets,
    onQuantityChange,
    aiBanner,
    toolsBanner,
    printingBanner
}) => {
    const CATEGORIES: CategoryDef[] = useMemo(
        () => [
            {
                id: 'printing',
                label: T._('Printing Methods', 'Admin'),
                items: PREDEFINED_PRINTING_METHODS,
                color: '#2196f3',
                saveLabel: T._('Save "{0}" as my Printing Method', 'Admin'),
                hasBuildYourOwn: true,
                buildYourOwnTooltip: T._('Create a custom printing method from scratch', 'Admin')
            },
            {
                id: 'pricing',
                label: T._('Pricing Rules', 'Admin'),
                items: PREDEFINED_PRICING_RULES,
                color: '#8b5cf6',
                saveLabel: T._('Save "{0}" as my Pricing Rule', 'Admin'),
                hasBuildYourOwn: true,
                buildYourOwnTooltip: T._('Create a custom pricing rule from scratch', 'Admin')
            },
            {
                id: 'tools',
                label: T._('Customization tools', 'Admin'),
                items: PREDEFINED_CUSTOMIZATION_TOOLS,
                color: '#f59e0b',
                saveLabel: T._('Activate "{0}"', 'Admin'),
                hasBuildYourOwn: false
            },
            {
                id: 'ai',
                label: T._('AI Creative Tools', 'Admin'),
                items: PREDEFINED_AI_TOOLS,
                color: '#ec4899',
                saveLabel: T._('Activate "{0}"', 'Admin'),
                hasBuildYourOwn: true,
                buildYourOwnTooltip: T._('Explore all AI Creative Tools', 'Admin')
            },
            {
                id: 'commerce',
                label: T._('Commerce rules', 'Admin'),
                items: PREDEFINED_COMMERCE_RULES,
                color: '#14b8a6',
                saveLabel: T._('Activate "{0}"', 'Admin'),
                hasBuildYourOwn: false
            },
            {
                id: 'modules',
                label: T._('Modules', 'Admin'),
                items: PREDEFINED_MODULES,
                color: '#6366f1',
                saveLabel: T._('Activate "{0}"', 'Admin'),
                hasBuildYourOwn: false
            }
        ],
        []
    );

    const COMING_SOON = T._('Coming soon...', 'Admin');

    const [activeTab, setActiveTab] = useState<CategoryId>('printing');
    // Whole-panel accordion: collapse to a slim bar to free vertical space for the preview below.
    const [collapsed, setCollapsed] = useState(false);
    const category = CATEGORIES.find((c) => c.id === activeTab)!;
    const selectedId = selections[activeTab] ?? null;
    const selectedItem = category.items.find((item) => item.id === selectedId) ?? null;

    // Pills the customizer reports as already active on the product are hidden — there's nothing to discover
    // about a feature the merchant already has. Ids are unique across categories, so filtering globally is safe.
    const hiddenSet = useMemo(() => new Set(hiddenTools ?? []), [hiddenTools]);
    const visibleItems = category.items.filter((item) => !hiddenSet.has(item.id));

    // The current active selection (category + item) across all tabs, resolved once for the "Active" row and
    // its description list below it.
    const activeEntries = CATEGORIES.map((cat) => {
        const selId = selections[cat.id];
        const item = selId ? cat.items.find((i) => i.id === selId) : undefined;
        return item ? { cat, item } : null;
    }).filter((entry): entry is { cat: CategoryDef; item: DiscoveryItem } => entry !== null);

    const handleItemClick = useCallback(
        (item: DiscoveryItem) => {
            onSelect(activeTab, selectedId === item.id ? null : item.id);
        },
        [activeTab, selectedId, onSelect]
    );

    if (collapsed) {
        return (
            <PanelContainer>
                <CollapsedBar>
                    <CollapsedLabel type='button' onClick={() => setCollapsed(false)}>
                        {T._('Discovery tools', 'Admin')}
                    </CollapsedLabel>
                    {activeEntries.length > 0 && <CollapsedCount>{activeEntries.length}</CollapsedCount>}
                    <ZTooltip position='bottom' title={T._('Expand', 'Admin')} enterDelay={300} exitDelay={0} interactive={false}>
                        <CollapseButton type='button' onClick={() => setCollapsed(false)} aria-label={T._('Expand discovery tools', 'Admin')}>
                            <Icon>
                                <ExpandMoreIcon />
                            </Icon>
                        </CollapseButton>
                    </ZTooltip>
                </CollapsedBar>
            </PanelContainer>
        );
    }

    return (
        <PanelContainer>
            {/* Category tabs */}
            <TabsRow>
                {CATEGORIES.map((cat) => (
                    <ZTooltip key={cat.id} position='bottom' title={cat.comingSoon ? COMING_SOON : ''} enterDelay={300} exitDelay={0} interactive={false}>
                        <CategoryTab
                            $active={activeTab === cat.id}
                            $color={cat.color}
                            $disabled={cat.comingSoon}
                            onClick={() => {
                                if (cat.comingSoon) return;
                                setActiveTab(cat.id);
                            }}
                        >
                            {cat.label}
                            {selections[cat.id] ? ' \u2022' : ''}
                        </CategoryTab>
                    </ZTooltip>
                ))}
                <ZTooltip position='bottom' title={T._('Collapse', 'Admin')} enterDelay={300} exitDelay={0} interactive={false}>
                    <CollapseButton type='button' onClick={() => setCollapsed(true)} aria-label={T._('Collapse discovery tools', 'Admin')}>
                        <Icon>
                            <ExpandLessIcon />
                        </Icon>
                    </CollapseButton>
                </ZTooltip>
            </TabsRow>

            {/* Items carousel for active tab */}
            <ContentRow>
                <CarouselWrapper>
                    <Carousel variant='default' gap={8} scrollAmount={200}>
                        {visibleItems.length === 0 && <EmptyNote>{T._('All of these are already active on this product.', 'Admin')}</EmptyNote>}
                        {visibleItems.map((item) => {
                            // Printing methods are only interactive once they have a payload wired in
                            // DISCOVERY_PRINTING_PAYLOADS; the rest are placeholders, so disable them.
                            // Items may also opt out explicitly via `disabled` (e.g. tools not yet hooked up).
                            const isWired = activeTab !== 'printing' || item.id in DISCOVERY_PRINTING_PAYLOADS;
                            // Temporarily-unavailable tools (item or whole category flagged) are disabled with a
                            // "Coming soon..." tooltip.
                            const isComingSoon = Boolean(item.comingSoon || category.comingSoon);
                            // Until the customizer signals it's fully loaded (and any variant dialog is
                            // dismissed), disable every pill: a selection streamed too early can't be applied.
                            const disabled = !customizerReady || !isWired || Boolean(item.disabled) || isComingSoon;
                            // Per-feature description is shown inline on the active pill (ActivePillDesc), not on
                            // hover — so a non-coming-soon, ready pill has no tooltip.
                            const tooltip = !customizerReady ? T._('Waiting for the customizer to finish loading…', 'Admin') : isComingSoon ? COMING_SOON : '';
                            return (
                                <ZTooltip key={item.id} position='bottom' title={tooltip} enterDelay={300} exitDelay={0} interactive={false}>
                                    <PillButton
                                        $selected={selectedId === item.id}
                                        $color={category.color}
                                        $disabled={disabled}
                                        aria-disabled={disabled}
                                        onClick={() => {
                                            if (disabled) return;
                                            handleItemClick(item);
                                        }}
                                    >
                                        {item.name}
                                    </PillButton>
                                </ZTooltip>
                            );
                        })}
                        {category.hasBuildYourOwn && (
                            <StickyEnd>
                                <ZTooltip position='bottom' title={category.buildYourOwnTooltip ?? ''} enterDelay={300} exitDelay={0} interactive={false}>
                                    <BuildYourOwnItem onClick={() => onBuildYourOwn(activeTab)}>{'+ ' + T._('Build your own', 'Admin')}</BuildYourOwnItem>
                                </ZTooltip>
                            </StickyEnd>
                        )}
                    </Carousel>
                </CarouselWrapper>
            </ContentRow>

            {/* CASE 1 "billed via credits" banner — under the AI badges, shown once an AI tool is pressed while
                the seller hasn't activated (the host gates `aiBanner` on the engaged-but-un-activated state). */}
            {activeTab === 'ai' && aiBanner}

            {/* Fill Background "import a background gallery" banner — under the Tools pills, shown once Fill
                Background is engaged by a legacy account with no gallery (the host gates `toolsBanner` on that). */}
            {activeTab === 'tools' && toolsBanner}

            {/* AI Vectorizer credit banner — under the Printing Methods pills, shown while that method is
                engaged (the host gates `printingBanner` on the selection and on the resolved credit case). */}
            {activeTab === 'printing' && printingBanner}

            {/* Simulated order quantity — only for the quantity-tiers rule, which prices by order quantity.
                Hidden while a Commerce rule owns the quantity, because a second control here would fight it:
                 - MOQ gives the customizer its own footer quantity selector (it renders whenever a quantity
                   rule exists) and snaps every value to the rule, so a preset below the minimum, or off-step,
                   would be snapped straight back and read as a broken button.
                 - Bulk Variation Forms takes the order quantity from the grid cells instead of the footer, so
                   a preset set here would simply have no effect on the price.
                While either is on, that rule's own control IS the demo. */}
            {activeTab === 'pricing' &&
                selectedId === 'quantity-tiers' &&
                selections.commerce !== 'minimum-order-quantity' &&
                selections.commerce !== 'bulk-variation-forms' &&
                onQuantityChange && (
                    <QuantityRow>
                        <span>{T._('Simulated quantity', 'Admin')}:</span>
                        <QuantityPills $color={category.color}>
                            {(quantityPresets ?? [1, 10, 50, 100]).map((preset) => (
                                <QuantityPill
                                    key={preset}
                                    $selected={quantity === preset}
                                    $color={category.color}
                                    onClick={() => onQuantityChange(preset)}
                                >
                                    {preset}
                                </QuantityPill>
                            ))}
                        </QuantityPills>
                    </QuantityRow>
                )}
            {/* Active selections across categories — a colored pill per selection, each showing its name and
                an always-visible description below it. The "Active" label rides the divider to leave the full
                row width to the chips (fits up to 6, one per category). */}
            {activeEntries.length > 0 && (
                <>
                    <ActiveDividerLabel>{T._('Active', 'Admin')}</ActiveDividerLabel>
                    <ActiveRow>
                        {activeEntries.map(({ cat, item }) => {
                            const saveTitle = cat.saveLabel.replace('{0}', item.name);
                            const removeTitle = T._('Remove', 'Admin');
                            return (
                                <ActivePill key={cat.id} $color={cat.color}>
                                    <ActivePillBody>
                                        <ActivePillName>{item.name}</ActivePillName>
                                        <ActivePillDescription text={item.description ?? ''} />
                                    </ActivePillBody>
                                    <PillActions $color={cat.color}>
                                        <ZTooltip position='bottom' title={saveTitle} enterDelay={300} exitDelay={0} interactive={false}>
                                            <PillActionButton $color={cat.color} aria-label={saveTitle} onClick={() => onSave(cat.id, item)}>
                                                <Icon>
                                                    <SaveIcon />
                                                </Icon>
                                            </PillActionButton>
                                        </ZTooltip>
                                        <ZTooltip position='bottom' title={removeTitle} enterDelay={300} exitDelay={0} interactive={false}>
                                            <PillActionButton $color={cat.color} aria-label={removeTitle} onClick={() => onSelect(cat.id, null)}>
                                                <PillActionIcon>
                                                    <CloseIcon />
                                                </PillActionIcon>
                                            </PillActionButton>
                                        </ZTooltip>
                                    </PillActions>
                                </ActivePill>
                            );
                        })}
                    </ActiveRow>
                </>
            )}
        </PanelContainer>
    );
};

export default DiscoveryModePanel;
